import type { BillingBudget } from '../types/billing';
import {
  BILLING_MONTHLY_BUDGET_FIELDS,
  BILLING_MONTHLY_SPEND_FIELDS,
} from '../types/billing';
import { getCalendarMonthBudgetIndex } from './billingBudgetValidation';

/**
 * conversion_rate is USD -> local currency (1 USD = conversion_rate units of the
 * account's currency), so a local-currency amount is converted to USD by dividing.
 * Missing/zero rates are treated as already-USD (rate of 1) rather than dividing by 0.
 */
export function toUsd(amount: number | null | undefined, conversionRate: number | null | undefined): number {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return 0;
  const rate = conversionRate && conversionRate > 0 ? conversionRate : 1;
  return amount / rate;
}

function usdField(row: BillingBudget, amount: number | null | undefined): number {
  return toUsd(amount, row.conversion_rate);
}

export interface BillingReportTotals {
  totalBudgetUsd: number;
  totalSpendUsd: number;
  totalSavingsUsd: number;
  totalOverspendUsd: number;
  utilizationPct: number;
  atRiskCount: number;
  accountCount: number;
}

/**
 * Net savings (budget - spend) summed across months strictly before the current
 * calendar month, in USD. Positive = under budget so far, negative = overspent so far.
 * The current (in-progress) month is excluded since it isn't closed yet.
 */
export function accountPastMonthsNetUsd(
  row: BillingBudget,
  calendarMonthIndex = getCalendarMonthBudgetIndex(),
): number {
  let net = 0;
  for (let i = 0; i < calendarMonthIndex; i++) {
    const budget = row[BILLING_MONTHLY_BUDGET_FIELDS[i]] as number | null;
    const spend = row[BILLING_MONTHLY_SPEND_FIELDS[i]] as number | null;
    net += usdField(row, budget ?? 0) - usdField(row, spend ?? 0);
  }
  return Math.round(net * 100) / 100;
}

export function computeReportTotals(rows: BillingBudget[]): BillingReportTotals {
  let totalBudgetUsd = 0;
  let totalSpendUsd = 0;
  let totalSavingsUsd = 0;
  let totalOverspendUsd = 0;
  let atRiskCount = 0;

  for (const row of rows) {
    const budgetUsd = usdField(row, row.annual_fixed_budget);
    const spendUsd = usdField(row, row.ytd_spend);
    totalBudgetUsd += budgetUsd;
    totalSpendUsd += spendUsd;

    const net = accountPastMonthsNetUsd(row);
    if (net >= 0) {
      totalSavingsUsd += net;
    } else {
      totalOverspendUsd += -net;
    }

    if (row.is_flagged || (budgetUsd > 0 && spendUsd > budgetUsd)) {
      atRiskCount += 1;
    }
  }

  const utilizationPct = totalBudgetUsd > 0 ? (totalSpendUsd / totalBudgetUsd) * 100 : 0;

  return {
    totalBudgetUsd,
    totalSpendUsd,
    totalSavingsUsd,
    totalOverspendUsd,
    utilizationPct,
    atRiskCount,
    accountCount: rows.length,
  };
}

export interface MonthlyTrendPoint {
  month: string;
  budget: number;
  spend: number;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function computeMonthlyTrend(rows: BillingBudget[]): MonthlyTrendPoint[] {
  return MONTH_LABELS.map((month, i) => {
    let budget = 0;
    let spend = 0;
    for (const row of rows) {
      budget += usdField(row, row[BILLING_MONTHLY_BUDGET_FIELDS[i]] as number | null);
      spend += usdField(row, row[BILLING_MONTHLY_SPEND_FIELDS[i]] as number | null);
    }
    return { month, budget: Math.round(budget), spend: Math.round(spend) };
  });
}

export interface GroupedSplitRow {
  name: string;
  budget: number;
  spend: number;
}

export type BillingSplitDimension = 'business_unit' | 'sub_bu' | 'cloud_type';

/** Top N groups by spend, with the remainder folded into "Other". */
export function computeGroupedSplit(
  rows: BillingBudget[],
  dimension: BillingSplitDimension,
  topN = 8,
): GroupedSplitRow[] {
  const totals = new Map<string, { budget: number; spend: number }>();
  for (const row of rows) {
    const key = (row[dimension] as string | null)?.trim() || 'Unassigned';
    const entry = totals.get(key) ?? { budget: 0, spend: 0 };
    entry.budget += usdField(row, row.annual_fixed_budget);
    entry.spend += usdField(row, row.ytd_spend);
    totals.set(key, entry);
  }

  const sorted = Array.from(totals.entries())
    .map(([name, v]) => ({ name, budget: Math.round(v.budget), spend: Math.round(v.spend) }))
    .sort((a, b) => b.spend - a.spend);

  if (sorted.length <= topN) return sorted;

  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const other = rest.reduce(
    (acc, r) => ({ name: 'Other', budget: acc.budget + r.budget, spend: acc.spend + r.spend }),
    { name: 'Other', budget: 0, spend: 0 },
  );
  return [...top, other];
}

export interface AccountMoverRow {
  accountId: string;
  accountName: string;
  amountUsd: number;
}

export function computeTopMovers(
  rows: BillingBudget[],
  limit = 5,
): { topSavings: AccountMoverRow[]; topOverspend: AccountMoverRow[] } {
  const withNet = rows.map((row) => ({
    accountId: row.account_id,
    accountName: row.account_name || row.account_id,
    net: accountPastMonthsNetUsd(row),
  }));

  const topSavings = withNet
    .filter((r) => r.net > 0)
    .sort((a, b) => b.net - a.net)
    .slice(0, limit)
    .map((r) => ({ accountId: r.accountId, accountName: r.accountName, amountUsd: Math.round(r.net) }));

  const topOverspend = withNet
    .filter((r) => r.net < 0)
    .sort((a, b) => a.net - b.net)
    .slice(0, limit)
    .map((r) => ({ accountId: r.accountId, accountName: r.accountName, amountUsd: Math.round(-r.net) }));

  return { topSavings, topOverspend };
}

export function formatUsdCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

export function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}
